import React from 'react';
import {
  getIdentityTransformMatrix,
} from '../../network/panZoom';
import {
  formatTradeValue,
} from '../../numberFormatters';
import {
  GraphExportType,
} from '../../Utils';
import {
  minCircleRadius,
  NodeSizing,
} from '../../workerStore/feasibility/Utils';
import {
  getTicksInfoGetter,
} from '../chart/getTicksInfo';
import {
  getNodeConstituents,
} from '../chart/Utils';
import {
  getAverageLineLabel,
} from '../container/Utils';
import {
  YAxisMeasure,
} from '../Utils';
import adjustVerticalTextAlignmentInfo from './adjustVerticalTextAlignment';
import Line from './Line';
import {
  bottomMargin,
  IExportInput as IProps,
  leftMargin,
  rightMargin,
  tickColor,
  tickLength,
  topMargin,
} from './Utils';

export default class extends React.PureComponent<IProps, {}> {
  private getTicksInfo = getTicksInfoGetter();

  render() {
    const {
      width, height, nodes, hideExports, yAxisMeasure,
      yAverage, xAxisMin, xAxisMax, total,
      yAxisMin, yAxisMax, exportType, year, nodeSizing,
    } = this.props;
    const fullWidth = width + leftMargin + rightMargin;
    const fullHeight = height + bottomMargin + topMargin;

    /* Start of nodes */
    const nodeElems = nodes.map(node => {
      const {
        id, x, y, color, radius, exportStatus, active,
      } = node;
      const radiusWithNodeSizing = (nodeSizing === NodeSizing.None) ? minCircleRadius : radius;
      const {circle, pieArc, interactiveOutline} = getNodeConstituents({
        x, y, exportStatus, color,
        hideExports, active,
        radius: radiusWithNodeSizing,
        isDynamic: false,
      });
      return (
        <g key={id}>
          {circle}
          {pieArc}
          {interactiveOutline}
        </g>
      );
    });
    const nodeGroup = (
        <g>{[...nodeElems]}</g>
    );
    /* End of nodes */

    const {
      xTickValues,
      yTickValues,
      transformedXScale,
      transformedYScale,
    } = this.getTicksInfo({
      svgWidth: width, svgHeight: height,
      xAxisMax, xAxisMin,
      yAxisMax, yAxisMin,
      transformationMatrix: getIdentityTransformMatrix(),
    });

    const xTickTop = height;
    const xTickBottom = height + tickLength;
    const yTickLeft = - tickLength;

    /* Start of colored bars */
    const coloredBars = nodes.map(({x, y, color, id}) => {
      const yBar = (
        <Line key={`y-colored-bar-${id}`}
          x1={yTickLeft} y1={y}
          x2={0} y2={y}
          color={color}/>
      );
      const xBar = (
        <Line key={`x-colored-bar-${id}`}
          x1={x} y1={xTickTop}
          x2={x} y2={xTickBottom}
          color={color}/>

      );
      return [xBar, yBar];
    });
    const coloredBarsGroup = (
      <g>{[...coloredBars]}</g>
    );
    /* End of colored bars */

    /* Start of tick marks */
    const xTicks = xTickValues.map(value => {
      const xCoord = transformedXScale(value);
      return (
        <Line key={`x-tick-${value}`}
          x1={xCoord} y1={xTickTop}
          x2={xCoord} y2={xTickBottom}/>
      );
    });
    const yTicks = yTickValues.map(value => {
      const yCoord = transformedYScale(value);
      return (
        <Line key={`y-tick-${value}`}
          x1={yTickLeft} y1={yCoord}
          x2={0} y2={yCoord}/>
      );
    });
    const ticksGroup = (
      <g>{[...xTicks, ...yTicks]}</g>
    );
    /* End of tick marks */

    /* Start of grid lines */
    const xGridLines = xTickValues.map(value => {
      const xCoord = transformedXScale(value);
      return (
        <Line key={`x-tick-${value}`}
          x1={xCoord} y1={0}
          x2={xCoord} y2={height}/>
      );
    });
    const yGridLines = yTickValues.map(value => {
      const yCoord = transformedYScale(value);
      return (
        <Line key={`y-tick-${value}`}
          x1={0} y1={yCoord}
          x2={width} y2={yCoord}/>
      );
    });
    const gridLinesGroup = (
      <g>{[...xGridLines, ...yGridLines]}</g>
    );
    /* End of grid lines */

    /* Start of axes */
    const xAxis = (
      <Line x1={0} y1={height} x2={width} y2={height}/>
    );

    const yAxis = (
      <Line x1={0} y1={0} x2={0} y2={height}/>
    );
    const axesGroup = (
      <g>
        {xAxis}
        {yAxis}
      </g>
    );
    /* End of axes */

    const tickLabelSeparation = 5;
    const yTickLabelXPosition = yTickLeft - tickLabelSeparation;
    const xTickLabelYPosition = xTickBottom + tickLabelSeparation;
    /* Start of tick labels */
    const yTickLabels = yTickValues.map(value => {
      const transformedY = transformedYScale(value);
      const {y, alignmentBaseline} = adjustVerticalTextAlignmentInfo(
        exportType === GraphExportType.PDF, transformedY, 'middle',
      );
      return (
        <text transform={`translate(${yTickLabelXPosition}, ${y})`}
          key={`y-tick-label-${value}`}
          textAnchor='end' alignmentBaseline={alignmentBaseline}>
          {value}
        </text>
      );
    });
    const xTickLabels = xTickValues.map(value => {
      const xCoord = transformedXScale(value);
      const {y, alignmentBaseline} = adjustVerticalTextAlignmentInfo(
        exportType === GraphExportType.PDF, xTickLabelYPosition, 'hanging',
      );
      return (
        <text transform={`translate(${xCoord}, ${y}) rotate(-65)`}
          key={`x-tick-label-${value}`}
          textAnchor='end' alignmentBaseline={alignmentBaseline}>
          {value}
        </text>
      );
    });
    const tickLabelsGroup = (
      <g>
        {xTickLabels}
        {yTickLabels}
      </g>
    );
    /* End of tick labels */

    const axisLabelSeparationFromEdge = 15;
    /* Start of axis labels */
    const xAxisLabel = (
      <text
        transform={`translate(${width / 2 + leftMargin}, ${fullHeight - axisLabelSeparationFromEdge})`}
        textAnchor='middle' alignmentBaseline='baseline'>
        {__lexiconText('axisName.distance')}
      </text>
    );

    const yAxisLabelText = (yAxisMeasure === YAxisMeasure.Complexity) ?
                            __lexiconText('axisName.complexity') :
                            __lexiconText('axisName.opportunityGain');
    const yAxisLabel = (
      <text transform={`translate(${axisLabelSeparationFromEdge}, ${topMargin + height / 2}) rotate(-90)`}
        textAnchor='middle' alignmentBaseline='baseline'>
        {yAxisLabelText}
      </text>
    );
    /* End of axis labels */

    /* Start of average line */
    const averageYCoord = transformedYScale(yAverage);
    const averageLineThickness = 2;
    const averageLine = (
      <line strokeWidth={averageLineThickness}
        stroke={tickColor} strokeDasharray='10, 10'
        x1={0} y1={averageYCoord}
        x2={width} y2={averageYCoord}/>
    );
    const {
      y: averageLabelYCoord,
      alignmentBaseline: averageLabelAlignmentBaseline,
    } = adjustVerticalTextAlignmentInfo(
      exportType === GraphExportType.PDF, averageYCoord + 10, 'hanging',
    );
    const averageLabel = (
      <text x={20} y={averageLabelYCoord} textAnchor='start'
        alignmentBaseline={averageLabelAlignmentBaseline}>
        {getAverageLineLabel(yAverage, year, this.props.productClass)}
      </text>
    );
    /* End of average line */

    // Total:
    const totalElem = (
      <text x={leftMargin + width / 2} y={topMargin / 2}>
        {formatTradeValue(total)}
      </text>
    );

    return (
      <svg viewBox={`0 0 ${fullWidth} ${fullHeight}`}
        xmlns='http://www.w3.org/2000/svg'>
        {totalElem}
        <g transform={`translate(${leftMargin}, ${topMargin})`}>
          {coloredBarsGroup}
          {ticksGroup}
          {gridLinesGroup}
          {axesGroup}
          {tickLabelsGroup}
          {averageLine}
          {averageLabel}
          {nodeGroup}
        </g>
        {xAxisLabel}
        {yAxisLabel}
      </svg>
    );
  }
}
